import { useCallback, useEffect, useState } from "react";
import i18n from "@/i18n";
import { supabase } from "@/lib/supabase";

export interface SoftwareVersion {
  version: string;
  date: string;
  notes: string;
}

export interface SoftwareEdition {
  key: string;
  label: string;
  price: number;
}

export interface SoftwareProduct {
  id: number;
  name: string;
  slug: string;
  category: string | null;
  summary: string | null;
  description: string | null;
  icon: string;
  latest_version: string | null;
  versions: SoftwareVersion[] | null;
  editions: SoftwareEdition[] | null;
  rating: number;
  downloads: number;
  status: string;
  sort_order: number;
}

interface UseSoftwareProductsResult {
  products: SoftwareProduct[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useSoftwareProducts(): UseSoftwareProductsResult {
  const [products, setProducts] = useState<SoftwareProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from("software_products")
        .select("*")
        .eq("status", "active")
        .order("sort_order", { ascending: true });

      if (queryError) throw queryError;
      setProducts((data as SoftwareProduct[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.t("products.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { products, loading, error, reload: load };
}